import { Link, useParams } from "react-router-dom";
import { articles } from "@/mocks/articles";
import useScrollToTop from "@/hooks/useScrollToTop";
import Footer from "../home/components/Footer";
import Navbar from "../home/components/Navbar";

export default function ArticlePage() {
  useScrollToTop();
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
            <p className="text-sm text-foreground-500 mb-6">The article you are looking for does not exist.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const faqSection = article.sections.find((s) => s.id === "faq");
  const hasFaqs = faqSection && "faqs" in faqSection && Array.isArray(faqSection.faqs);

  return (
    <>
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-400">
              <li><Link to="/" className="hover:text-foreground-700 transition-colors whitespace-nowrap">Home</Link></li>
              <li className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-700 font-medium">{article.title.length > 40 ? article.title.slice(0, 40) + "..." : article.title}</li>
            </ol>
          </nav>

          {/* Article Header */}
          <header className="mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              {article.category}
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-400">
              <span>Updated: {article.updated}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.readTime}</span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="relative w-full h-56 md:h-80 rounded-xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
          </div>

          {/* Table of Contents */}
          <nav className="mb-10 p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70" aria-label="Table of Contents">
            <h2 className="text-sm font-semibold text-foreground-900 mb-3">In This Guide</h2>
            <ol className="space-y-2">
              {article.sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`} className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* Article Body */}
          {article.sections.map((section) => (
            <section key={section.id} id={section.id} className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                {section.title}
              </h2>
              {section.content && (
                <div
                  className="article-body space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              )}
              {section.id === "faq" && hasFaqs && faqSection && "faqs" in faqSection && Array.isArray(faqSection.faqs) && (
                <div className="space-y-3 mt-4">
                  {faqSection.faqs.map((faq, idx) => (
                    <details key={idx} className="group rounded-xl border border-background-200/70 bg-white overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-50 transition-colors">
                        <span className="text-sm font-medium text-foreground-800">{faq.q}</span>
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-arrow-down-s-line text-foreground-400 group-open:hidden"></i>
                          <i className="ri-arrow-up-s-line text-foreground-400 hidden group-open:block"></i>
                        </span>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </section>
          ))}

          {/* Internal linking back to homepage */}
          <div className="py-6 px-5 md:px-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
            <p className="text-sm text-foreground-600 mb-3">
              Want to explore more guides or compare platforms?
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              Back to GTBuy Sheet Homepage
            </Link>
          </div>

          {/* Related Articles */}
          <div className="mt-12">
            <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">Related Articles</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {articles
                .filter((a) => a.slug !== article.slug)
                .slice(0, 2)
                .map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="group flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={related.image}
                        alt={related.title}
                        title={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex flex-col justify-center">
                      <span className="text-xs text-primary-600 font-medium mb-1">{related.category}</span>
                      <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-2 leading-snug">
                        {related.title}
                      </h4>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </article>

        {/* Schema.org structured data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: (() => {
            const siteUrl = typeof import.meta !== "undefined" && import.meta.env ? (import.meta.env.VITE_SITE_URL || "https://gtbuyspreadsheet.wtf") : "https://gtbuyspreadsheet.wtf";
            const pageUrl = `${siteUrl}/article/${article.slug}`;
            const json: Record<string, unknown>[] = [
              {
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": article.title,
                "description": article.excerpt,
                "author": { "@type": "Organization", "name": "GTBuy Sheet" },
                "publisher": {
                  "@type": "Organization",
                  "name": "GTBuy Sheet",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/54d75be42d5913aa14ca64dd22e5d1e3.jpeg"
                  }
                },
                "datePublished": "2026-07-01",
                "dateModified": "2026-07-01",
                "mainEntityOfPage": { "@type": "WebPage", "@id": pageUrl },
                "image": article.image,
              },
            ];
            if (hasFaqs && faqSection && "faqs" in faqSection && Array.isArray(faqSection.faqs)) {
              json.push({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqSection.faqs.map((faq: { q: string; a: string }) => ({
                  "@type": "Question",
                  "name": faq.q,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.a,
                  },
                })),
              });
            }
            json.push({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
                { "@type": "ListItem", "position": 2, "name": article.title.length > 60 ? article.title.slice(0, 60) + "..." : article.title, "item": pageUrl },
              ],
            });
            return JSON.stringify(json);
          })(),
        }} />
      </main>
      <Footer />
    </>
  );
}